import React, { useEffect, useState } from 'react';
import { adminAPI, orgAPI, learningAPI } from '../api/client';
import Breadcrumb from '../components/Breadcrumb';
import { Users, Building, BookOpen, Edit, Trash2, X, Plus } from 'lucide-react';

const Admin = () => {
  const [activeTab, setActiveTab] = useState('users');

  return (
    <div>
      <Breadcrumb items={[{ label: 'Admin Panel', path: null }]} showBackButton={false} />
      
      <h1 className="text-3xl font-bold text-gray-900 mb-8">Admin Paneli</h1>

      {/* Tabs */}
      <div className="mb-6 border-b border-gray-200">
        <div className="flex gap-4">
          <button
            onClick={() => setActiveTab('users')}
            className={`px-4 py-2 border-b-2 transition-colors ${
              activeTab === 'users'
                ? 'border-blue-600 text-blue-600 font-medium'
                : 'border-transparent text-gray-600 hover:text-gray-900'
            }`}
          >
            <Users className="inline mr-2" size={20} />
            Kullanıcılar
          </button>
          <button
            onClick={() => setActiveTab('orgs')}
            className={`px-4 py-2 border-b-2 transition-colors ${
              activeTab === 'orgs'
                ? 'border-blue-600 text-blue-600 font-medium'
                : 'border-transparent text-gray-600 hover:text-gray-900'
            }`}
          >
            <Building className="inline mr-2" size={20} />
            Organizasyonlar
          </button>
          <button
            onClick={() => setActiveTab('courses')}
            className={`px-4 py-2 border-b-2 transition-colors ${
              activeTab === 'courses'
                ? 'border-blue-600 text-blue-600 font-medium'
                : 'border-transparent text-gray-600 hover:text-gray-900'
            }`}
          >
            <BookOpen className="inline mr-2" size={20} />
            Kurslar
          </button>
        </div>
      </div>

      {/* Tab Content */}
      {activeTab === 'users' && <UsersTab />}
      {activeTab === 'orgs' && <OrganizationsTab />}
      {activeTab === 'courses' && <CoursesTab />}
    </div>
  );
};

const UsersTab = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      const response = await adminAPI.listUsers();
      setUsers(response.data);
    } catch (error) {
      console.error('Kullanıcılar yüklenemedi:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleActive = async (userId, currentStatus) => {
    try {
      await adminAPI.updateUser(userId, { isActive: !currentStatus });
      await loadUsers();
    } catch (error) {
      console.error('Durum güncellenemedi:', error);
      alert('Durum güncellenemedi');
    }
  };

  const handleDeleteUser = async (userId, userEmail) => {
    if (!window.confirm(`"${userEmail}" kullanıcısını silmek istediğinize emin misiniz? Bu işlem geri alınamaz.`)) {
      return;
    }
    try {
      await adminAPI.deleteUser(userId);
      alert('Kullanıcı başarıyla silindi');
      await loadUsers();
    } catch (error) {
      console.error('Kullanıcı silinemedi:', error);
      alert('Kullanıcı silinemedi: ' + (error.response?.data?.detail || error.message));
    }
  };

  if (loading) {
    return <div className="text-center py-8">Yükleniyor...</div>;
  }

  return (
    <div>
      <div className="flex justify-end mb-4">
        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          <Plus size={20} />
          Yeni Kullanıcı Ekle
        </button>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">İsim</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Durum</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">İşlemler</th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {users.map((user) => (
            <tr key={user.id}>
              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                {user.fullName || `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                {user.email}
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                  user.isActive 
                    ? 'bg-green-100 text-green-800' 
                    : 'bg-red-100 text-red-800'
                }`}>
                  {user.isActive ? 'Aktif' : 'Pasif'}
                </span>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm">
                <button
                  onClick={() => { setSelectedUser(user); setShowRoleModal(true); }}
                  className="text-indigo-600 hover:text-indigo-900 mr-3"
                >
                  Roller
                </button>
                <button
                  onClick={() => { setSelectedUser(user); setShowPasswordModal(true); }}
                  className="text-orange-600 hover:text-orange-900 mr-3"
                  data-testid={`change-password-${user.id}`}
                >
                  Şifre Değiştir
                </button>
                <button
                  onClick={() => handleToggleActive(user.id, user.isActive)}
                  className="text-blue-600 hover:text-blue-900 mr-3"
                >
                  {user.isActive ? 'Devre Dışı' : 'Aktif Et'}
                </button>
                <button
                  onClick={() => handleDeleteUser(user.id, user.email)}
                  className="text-red-600 hover:text-red-900"
                  data-testid={`delete-user-${user.id}`}
                >
                  <Trash2 size={16} className="inline" /> Sil
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>

      {showCreateModal && (
        <CreateUserModal
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => {
            setShowCreateModal(false);
            loadUsers();
          }}
        />
      )}

      {showRoleModal && selectedUser && (
        <RoleManagementModal
          user={selectedUser}
          onClose={() => { setShowRoleModal(false); setSelectedUser(null); }}
          onSuccess={() => {
            setShowRoleModal(false);
            setSelectedUser(null);
            loadUsers();
          }}
        />
      )}

      {showPasswordModal && selectedUser && (
        <ChangePasswordModal
          user={selectedUser}
          onClose={() => { setShowPasswordModal(false); setSelectedUser(null); }}
          onSuccess={() => {
            setShowPasswordModal(false);
            setSelectedUser(null);
            alert('Şifre başarıyla değiştirildi');
          }}
        />
      )}
    </div>
  );
};

const OrganizationsTab = () => {
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadOrgs();
  }, []);

  const loadOrgs = async () => {
    try {
      const response = await orgAPI.list();
      setOrgs(response.data);
    } catch (error) {
      console.error('Organizasyonlar yüklenemedi:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="text-center py-8">Yükleniyor...</div>;
  }

  return (
    <div className="bg-white rounded-lg shadow overflow-hidden">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Okul Adı</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Şehir</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">İletişim</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Durum</th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {orgs.map((org) => (
            <tr key={org.id}>
              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                {org.name}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                {org.city}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                {org.contactEmail}
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                  org.isActive 
                    ? 'bg-green-100 text-green-800' 
                    : 'bg-red-100 text-red-800'
                }`}>
                  {org.isActive ? 'Aktif' : 'Pasif'}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const CoursesTab = () => {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadCourses();
  }, []);

  const loadCourses = async () => {
    try {
      const response = await learningAPI.listCourses();
      setCourses(response.data);
    } catch (error) {
      console.error('Kurslar yüklenemedi:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (courseId) => {
    if (!window.confirm('Bu kursu silmek istediğinizden emin misiniz?')) return;
    
    try {
      await adminAPI.deleteCourse(courseId);
      await loadCourses();
    } catch (error) {
      console.error('Kurs silinemedi:', error);
      alert('Kurs silinemedi');
    }
  };

  if (loading) {
    return <div className="text-center py-8">Yükleniyor...</div>;
  }

  return (
    <div className="bg-white rounded-lg shadow overflow-hidden">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Kurs Adı</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Kategori</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Süre</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">İşlemler</th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {courses.map((course) => (
            <tr key={course.id}>
              <td className="px-6 py-4 text-sm font-medium text-gray-900">
                {course.title}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                {course.category}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                {course.duration ? `${course.duration} dk` : '-'}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm">
                <button
                  onClick={() => handleDelete(course.id)}
                  className="text-red-600 hover:text-red-900"
                >
                  <Trash2 size={18} />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};


const ChangePasswordModal = ({ user, onClose, onSuccess }) => {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (newPassword !== confirmPassword) {
      setError('Şifreler eşleşmiyor');
      return;
    }

    if (newPassword.length < 8) {
      setError('Şifre en az 8 karakter olmalıdır');
      return;
    }

    setLoading(true);
    try {
      await adminAPI.changePassword(user.id, newPassword);
      onSuccess();
    } catch (error) {
      setError(error.response?.data?.detail || 'Şifre değiştirilemedi');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md" data-testid="change-password-modal">
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-lg font-semibold">Şifre Değiştir</h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          <div className="p-3 bg-gray-50 rounded-lg">
            <p className="text-sm text-gray-500">Kullanıcı</p>
            <p className="font-medium">{user.fullName || user.email}</p>
            <p className="text-sm text-gray-500">{user.email}</p>
          </div>

          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {error}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Yeni Şifre *</label>
            <input
              type="password"
              required
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="En az 8 karakter"
              data-testid="new-password-input"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Şifre Tekrar *</label>
            <input
              type="password"
              required
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="Şifreyi tekrar girin"
              data-testid="confirm-password-input"
            />
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border rounded-lg hover:bg-gray-50"
            >
              İptal
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50"
              data-testid="submit-password-change"
            >
              {loading ? 'Kaydediliyor...' : 'Şifreyi Değiştir'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};


const CreateUserModal = ({ onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    phone: '',
  });
  const [selectedRole, setSelectedRole] = useState('TEACHER');
  const [selectedOrgId, setSelectedOrgId] = useState('');
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingOrgs, setLoadingOrgs] = useState(true);

  const availableRoles = [
    { value: 'SUPER_ADMIN', label: 'Süper Admin', needsOrg: false },
    { value: 'TRAINER', label: 'Eğitmen (YÖMEV)', needsOrg: false },
    { value: 'ACCREDITATION_COACH', label: 'Akreditasyon Koçu', needsOrg: false },
    { value: 'ORG_OWNER', label: 'Okul Sahibi', needsOrg: true },
    { value: 'ORG_COORDINATOR', label: 'Okul Koordinatörü', needsOrg: true },
    { value: 'TEACHER', label: 'Öğretmen', needsOrg: true },
    { value: 'PARENT', label: 'Veli', needsOrg: true },
    { value: 'VIEW_ONLY', label: 'Sadece Görüntüleme', needsOrg: true },
  ];

  useEffect(() => {
    loadOrganizations();
  }, []);

  const loadOrganizations = async () => {
    try {
      const response = await orgAPI.listOrganizations();
      setOrganizations(response.data);
      if (response.data.length > 0) {
        setSelectedOrgId(response.data[0].id);
      }
    } catch (error) {
      console.error('Organizasyonlar yüklenemedi:', error);
    } finally {
      setLoadingOrgs(false);
    }
  };

  const selectedRoleInfo = availableRoles.find(r => r.value === selectedRole);
  const needsOrganization = selectedRoleInfo?.needsOrg ?? true;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // 1. Create user
      const userResponse = await adminAPI.createUser(formData);
      const newUserId = userResponse.data.user.id;

      // 2. Assign role
      const roleData = {
        role: selectedRole,
        organizationId: needsOrganization ? selectedOrgId : null
      };
      await adminAPI.assignRole(newUserId, roleData);

      alert('Kullanıcı başarıyla oluşturuldu ve rol atandı!');
      onSuccess();
    } catch (error) {
      console.error('Kullanıcı oluşturulamadı:', error);
      alert('Kullanıcı oluşturulamadı: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-gray-900">Yeni Kullanıcı Ekle</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Ad *</label>
              <input
                type="text"
                required
                value={formData.firstName}
                onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Soyad *</label>
              <input
                type="text"
                required
                value={formData.lastName}
                onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Email *</label>
            <input
              type="email"
              required
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Şifre *</label>
            <input
              type="password"
              required
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              minLength={8}
              placeholder="En az 8 karakter, 1 büyük harf, 1 rakam"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Telefon</label>
            <input
              type="tel"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Role Selection */}
          <div className="border-t pt-4 mt-4">
            <h3 className="text-sm font-semibold text-gray-900 mb-3">Rol Ataması</h3>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Rol *</label>
              <select
                value={selectedRole}
                onChange={(e) => setSelectedRole(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                {availableRoles.map(role => (
                  <option key={role.value} value={role.value}>{role.label}</option>
                ))}
              </select>
            </div>

            {needsOrganization && (
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Organizasyon *</label>
                {loadingOrgs ? (
                  <p className="text-sm text-gray-500">Yükleniyor...</p>
                ) : organizations.length === 0 ? (
                  <p className="text-sm text-red-500">Önce bir organizasyon oluşturmalısınız</p>
                ) : (
                  <select
                    value={selectedOrgId}
                    onChange={(e) => setSelectedOrgId(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    required
                  >
                    {organizations.map(org => (
                      <option key={org.id} value={org.id}>{org.name}</option>
                    ))}
                  </select>
                )}
              </div>
            )}
          </div>

          <div className="flex gap-4 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
            >
              İptal
            </button>
            <button
              type="submit"
              disabled={loading || (needsOrganization && !selectedOrgId)}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {loading ? 'Oluşturuluyor...' : 'Kullanıcı Oluştur'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const RoleManagementModal = ({ user, onClose, onSuccess }) => {
  const [userRoles, setUserRoles] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddRoleForm, setShowAddRoleForm] = useState(false);
  const [newRole, setNewRole] = useState({
    role: 'TEACHER',
    organizationId: ''
  });

  const availableRoles = [
    { value: 'SUPER_ADMIN', label: 'Süper Admin', description: 'Tüm sisteme erişim' },
    { value: 'TRAINER', label: 'Eğitmen', description: 'Eğitim içerikleri yönetimi' },
    { value: 'ACCREDITATION_COACH', label: 'Akreditasyon Koçu', description: 'Akreditasyon süreçleri' },
    { value: 'SCHOOL_ADMIN', label: 'Okul Yöneticisi', description: 'Okul düzeyinde yönetim' },
    { value: 'TEACHER', label: 'Öğretmen', description: 'Ders içerikleri erişimi' },
    { value: 'COORDINATOR', label: 'Koordinatör', description: 'Program koordinasyonu' }
  ];

  useEffect(() => {
    loadUserRoles();
    loadOrganizations();
  }, []);

  const loadUserRoles = async () => {
    try {
      const response = await adminAPI.getUserRoles(user.id);
      setUserRoles(response.data);
    } catch (error) {
      console.error('Roller yüklenemedi:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadOrganizations = async () => {
    try {
      const response = await orgAPI.list();
      setOrganizations(response.data);
    } catch (error) {
      console.error('Organizasyonlar yüklenemedi:', error);
    }
  };

  const handleAddRole = async (e) => {
    e.preventDefault();
    try {
      await adminAPI.assignRole(user.id, {
        role: newRole.role,
        organizationId: newRole.organizationId || null
      });
      alert('Rol başarıyla eklendi!');
      setShowAddRoleForm(false);
      setNewRole({ role: 'TEACHER', organizationId: '' });
      loadUserRoles();
    } catch (error) {
      console.error('Rol eklenemedi:', error);
      alert('Rol eklenirken hata oluştu: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleRemoveRole = async (roleId) => {
    if (!window.confirm('Bu rolü kaldırmak istediğinize emin misiniz?')) return;
    
    try {
      await adminAPI.removeRole(user.id, roleId);
      alert('Rol başarıyla kaldırıldı!');
      loadUserRoles();
    } catch (error) {
      console.error('Rol kaldırılamadı:', error);
      alert('Rol kaldırılırken hata oluştu: ' + (error.response?.data?.detail || error.message));
    }
  };

  const getRoleLabel = (roleValue) => {
    return availableRoles.find(r => r.value === roleValue)?.label || roleValue;
  };

  const getOrgName = (orgId) => {
    return organizations.find(o => o.id === orgId)?.name || 'Bilinmeyen Okul';
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-8">Yükleniyor...</div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Rol Yönetimi</h2>
            <p className="text-sm text-gray-600 mt-1">{user.fullName} ({user.email})</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X size={24} />
          </button>
        </div>

        <div className="p-6">
          {/* Mevcut Roller */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold mb-3">Mevcut Roller</h3>
            {userRoles.length === 0 ? (
              <p className="text-gray-500 text-sm py-4 bg-gray-50 rounded-lg text-center">
                Henüz rol atanmamış
              </p>
            ) : (
              <div className="space-y-2">
                {userRoles.map((roleAssignment) => (
                  <div key={roleAssignment.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className={`px-2 py-1 text-xs font-semibold rounded ${
                          roleAssignment.role === 'SUPER_ADMIN' 
                            ? 'bg-purple-100 text-purple-800' 
                            : 'bg-blue-100 text-blue-800'
                        }`}>
                          {getRoleLabel(roleAssignment.role)}
                        </span>
                        {roleAssignment.organizationId && (
                          <span className="text-sm text-gray-600">
                            @ {getOrgName(roleAssignment.organizationId)}
                          </span>
                        )}
                        {!roleAssignment.organizationId && roleAssignment.role === 'SUPER_ADMIN' && (
                          <span className="text-xs text-purple-600">(Global)</span>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={() => handleRemoveRole(roleAssignment.id)}
                      className="p-1 text-red-600 hover:bg-red-50 rounded"
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Rol Ekle */}
          <div>
            {!showAddRoleForm ? (
              <button
                onClick={() => setShowAddRoleForm(true)}
                className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
              >
                <Plus size={18} />
                Yeni Rol Ekle
              </button>
            ) : (
              <form onSubmit={handleAddRole} className="border rounded-lg p-4 bg-indigo-50">
                <h4 className="font-semibold mb-3">Yeni Rol Ekle</h4>
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Rol *</label>
                    <select
                      value={newRole.role}
                      onChange={(e) => setNewRole({ ...newRole, role: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                    >
                      {availableRoles.map(role => (
                        <option key={role.value} value={role.value}>
                          {role.label} - {role.description}
                        </option>
                      ))}
                    </select>
                  </div>

                  {newRole.role !== 'SUPER_ADMIN' && newRole.role !== 'TRAINER' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Organizasyon {newRole.role !== 'ACCREDITATION_COACH' && '*'}
                      </label>
                      <select
                        value={newRole.organizationId}
                        onChange={(e) => setNewRole({ ...newRole, organizationId: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                        required={newRole.role !== 'ACCREDITATION_COACH'}
                      >
                        <option value="">Seçiniz...</option>
                        {organizations.map(org => (
                          <option key={org.id} value={org.id}>{org.name}</option>
                        ))}
                      </select>
                    </div>
                  )}

                  {newRole.role === 'SUPER_ADMIN' && (
                    <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
                      <p className="text-sm text-purple-800">
                        ⚠️ <strong>SUPER_ADMIN</strong> rolü, kullanıcıya tüm sistemde tam yetkili erişim verir. Bu rolü sadece güvendiğiniz kişilere verin.
                      </p>
                    </div>
                  )}
                </div>

                <div className="flex gap-3 mt-4">
                  <button
                    type="button"
                    onClick={() => { setShowAddRoleForm(false); setNewRole({ role: 'TEACHER', organizationId: '' }); }}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    İptal
                  </button>
                  <button
                    type="submit"
                    className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                  >
                    Rol Ekle
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>

        <div className="sticky bottom-0 bg-gray-50 px-6 py-4 border-t">
          <button
            onClick={onClose}
            className="w-full px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
          >
            Kapat
          </button>
        </div>
      </div>
    </div>
  );
};


export default Admin;
